import { httpClient } from '../api/http-client';
import { API_ENDPOINTS } from '../../constants/api';

export interface User {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: 'administrador' | 'miembro';
  estado: boolean;
  requiereCambioContrasena: boolean;
  ultimoAcceso?: string;
  roles: UserRole[];
  membresiaId?: string;
  creadoEn: string;
  actualizadoEn: string;
  membresia?: Membership;
}

export interface UserRole {
  id: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
  modulos: UserModule[];
}

export interface UserModule {
  id: string;
  nombre: string;
  descripcion?: string;
  ruta: string;
  icono: string;
  orden: number;
  tipo: string;
  moduloPadreId?: string;
  activo: boolean;
  permisos: UserPermission[];
}

export interface UserPermission {
  id: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface Role {
  id: string;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface Membership {
  id: string;
  nombres: string;
  apellidos: string;
  email: string;
  telefono: string;
  numeroDocumento?: string;
}


export interface CreateUserDto {
  email: string;
  password: string;
  nombreCompleto: string;
  tipoUsuario: 'administrador' | 'miembro';
  roles?: string[];
  membresiaId?: string;
}

export interface UpdateUserDto {
  email?: string;
  password?: string;
  nombreCompleto?: string;
  tipoUsuario?: 'administrador' | 'miembro';
  estado?: boolean;
  requiereCambioContrasena?: boolean;
  roles?: string[];
  membresiaId?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PaginatedUsersResponse {
  data: User[];
  meta: PaginationMeta;
}

export class UsersService {
  static async getUsers(
    page: number = 1, 
    limit: number = 10, 
    tipoUsuario?: 'administrador' | 'miembro'
  ): Promise<PaginatedUsersResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    if (tipoUsuario) {
      params.append('tipoUsuario', tipoUsuario);
    }

    const response = await httpClient.get<{ data: PaginatedUsersResponse }>(
      `${API_ENDPOINTS.users.list}?${params}`
    );
    
    return response.data.data;
  }

  static async searchUsers(
    searchTerm: string,
    page: number = 1, 
    limit: number = 10, 
    tipoUsuario?: 'administrador' | 'miembro'
  ): Promise<PaginatedUsersResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: searchTerm
    });

    if (tipoUsuario) {
      params.append('tipoUsuario', tipoUsuario);
    }

    const response = await httpClient.get<{ data: PaginatedUsersResponse }>(
      `${API_ENDPOINTS.users.list}?${params}`
    );
    
    return response.data.data;
  }

  static async getUserById(id: string): Promise<User> {
    const response = await httpClient.get<{ data: User }>(
      `${API_ENDPOINTS.users.list}/${id}`
    );
    
    return response.data.data;
  }

  static async createUser(userData: CreateUserDto): Promise<User> {
    const response = await httpClient.post<{ data: User }>(
      API_ENDPOINTS.users.create,
      userData
    );
    
    return response.data.data;
  }

  static async updateUser(id: string, userData: UpdateUserDto): Promise<User> {
    const response = await httpClient.put<{ data: User }>(
      API_ENDPOINTS.users.update(id),
      userData
    );
    
    return response.data.data;
  }

  static async deleteUser(id: string): Promise<void> {
    await httpClient.delete(API_ENDPOINTS.users.delete(id));
  }

  static async toggleUserStatus(id: string): Promise<User> {
    const response = await httpClient.post<{ data: User }>(
      API_ENDPOINTS.users.toggleStatus(id)
    );
    
    return response.data.data;
  }

  static async getRoles(): Promise<Role[]> {
    const response = await httpClient.get<Role[] | { data: Role[] }>(
      API_ENDPOINTS.users.roles
    );
    
    // El backend puede devolver directamente el array o envuelto en data
    return Array.isArray(response.data) ? response.data : response.data.data;
  }
}